import { ethers } from 'ethers';

// Theme and Menu handling
function initializeTheme() {
    // Check for saved theme preference or system preference
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
        document.documentElement.classList.add('dark');
    } else {
        document.documentElement.classList.remove('dark');
    }
}

function toggleTheme() {
    const isDark = document.documentElement.classList.toggle('dark');
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    
    // Update both theme icons
    const themeIcon = document.getElementById('theme-icon');
    const mobileThemeIcon = document.getElementById('theme-icon-mobile');
    const icon = isDark ? '🌙' : '☀️';
    
    if (themeIcon) themeIcon.textContent = icon;
    if (mobileThemeIcon) mobileThemeIcon.textContent = icon;
}

function toggleMenu() {
    const sidebar = document.getElementById('sidebar');
    const overlay = document.getElementById('overlay');
    
    if (sidebar && overlay) {
        sidebar.classList.toggle('show');
        overlay.classList.toggle('hidden');
    }
}

// Make toggles available globally
window.toggleTheme = toggleTheme;
window.toggleMenu = toggleMenu;

// Initialize on load
document.addEventListener('DOMContentLoaded', () => {
    initializeTheme();
    
    // Handle window resize
    window.addEventListener('resize', () => {
        if (window.innerWidth >= 1024) {
            const sidebar = document.getElementById('sidebar');
            const overlay = document.getElementById('overlay');
            if (sidebar && sidebar.classList.contains('show')) {
                sidebar.classList.remove('show');
            }
            if (overlay && !overlay.classList.contains('hidden')) {
                overlay.classList.add('hidden');
            }
        }
    });
});

const DLP_CONTRACT_ADDRESS = '0xff14346dF2B8Fd0c95BF34f1c92e49417b508AD5';
const VANA_RPC_URL = 'https://rpc.vana.org';
const IMPLEMENTATION_SLOT = "0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc";

// Store DLP data and sorting state
let dlpData = {
    dlps: [],
    totalDLPsCount: 0,
    totalStakeAmount: 0n,
    epochRewardAmount: 0n,
    stakingAPY: 0
};

// Initialize sort state to stake amount descending
let currentSort = {
    column: 'stakeAmount',
    direction: 'desc'
};

async function getImplementationAddress(provider) {
    const implementationSlot = await provider.getStorage(DLP_CONTRACT_ADDRESS, IMPLEMENTATION_SLOT);
    return ethers.getAddress('0x' + implementationSlot.slice(-40));
}

async function getContractABI(address) {
    const VANA_API_URL = `https://api.vanascan.io/api?module=contract&action=getabi&address=${address}`;
    const response = await fetch(VANA_API_URL);
    const data = await response.json();

    if (data.status !== '1') {
        throw new Error(`Failed to fetch ABI: ${data.result}`);
    }

    return JSON.parse(data.result);
}

function safeArrayAccess(arr, index, defaultValue = '') {
    if (!Array.isArray(arr) || index >= arr.length) {
        return defaultValue;
    }
    return arr[index] || defaultValue;
}

function sortDLPs(dlps, column, direction) {
    return [...dlps].sort((a, b) => {
        let aValue = a[column];
        let bValue = b[column];
        
        // Handle string comparison for name column
        if (column === 'name') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
            return direction === 'asc' 
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        }
        
        // Numeric comparison for other columns
        const multiplier = direction === 'asc' ? 1 : -1;
        return (aValue - bValue) * multiplier;
    });
}

function calculateStakingAPY(cycleReward, totalStake) {
    if (totalStake === 0n) return 0;
    
    // Convert BigInt values to numbers in VANA units
    const cycleRewardVana = Number(ethers.formatEther(cycleReward));
    const totalStakeVana = Number(ethers.formatEther(totalStake));
    
    // Calculate periodic rate (daily)
    const periodicRate = ((cycleRewardVana * 0.5) / totalStakeVana) / 21;
    
    // Calculate compound APY: (1 + r)^n - 1
    const apy = (Math.pow(1 + periodicRate, 365) - 1) * 100;
    
    return apy;
}

function markTopStakes(dlps) {
    // Sort by stake amount to identify top 16
    const sortedByStake = [...dlps].sort((a, b) => b.stakeAmount - a.stakeAmount);
    const top16Stakes = new Set(sortedByStake.slice(0, 16).map(dlp => dlp.stakeAmount));
    
    // Mark each DLP if it's in top 16
    return dlps.map(dlp => ({
        ...dlp,
        isTopStake: top16Stakes.has(dlp.stakeAmount)
    }));
}

async function getDLPs() {
    try {
        const provider = new ethers.JsonRpcProvider(VANA_RPC_URL, {
            chainId: 1480,
            name: 'vana-mainnet'
        });

        // Get implementation address and ABI
        console.log('Getting implementation address...');
        const implementationAddress = await getImplementationAddress(provider);
        console.log('Implementation address:', implementationAddress);

        console.log('Fetching ABI...');
        const abi = await getContractABI(implementationAddress);
        console.log('Contract ABI:', abi);

        // Create contract instance
        const contract = new ethers.Contract(DLP_CONTRACT_ADDRESS, abi, provider);
        
        // Get total DLPs count and epoch reward amount
        const [totalDLPsCount, epochRewardAmount] = await Promise.all([
            contract.dlpsCount(),
            contract.epochRewardAmount()
        ]);

        console.log('Total DLPs count:', totalDLPsCount);
        console.log('Epoch Reward Amount:', epochRewardAmount);

        // Create array with length + 1 to match contract indexing (1-based)
        const dlps = new Array(Number(totalDLPsCount) + 1).fill(null);
        let totalStakeAmount = 0n;

        // Fetch DLPs starting from index 1
        for (let i = 1; i <= Number(totalDLPsCount); i++) {
            try {
                console.log(`Fetching DLP ${i}...`);
                const dlpInfo = await contract.dlps(i);

                if (!dlpInfo) {
                    throw new Error('DLP info is null');
                }

                // Debug log full DLP info
                console.log(`Full DLP ${i} info:`, dlpInfo);

                // Safely access DLP properties
                const name = safeArrayAccess(dlpInfo, 6);
                const isValidDLP = name && name !== '' && !name.includes('0x0000000000000000000000000000000000000000');

                if (isValidDLP) {
                    console.log(`DLP ${i} is valid with name: ${name}`);
                    
                    const stakeAmount = safeArrayAccess(dlpInfo, 12, 0n);
                    const isVerified = safeArrayAccess(dlpInfo, 14, false);

                    if (typeof stakeAmount === 'bigint') {
                        totalStakeAmount += stakeAmount;
                    }

                    dlps[i] = {
                        id: i.toString(),
                        name: name,
                        logo: safeArrayAccess(dlpInfo, 7),
                        website: safeArrayAccess(dlpInfo, 8),
                        description: safeArrayAccess(dlpInfo, 9),
                        stakeAmount: typeof stakeAmount === 'bigint' ? Number(ethers.formatEther(stakeAmount)) : 0,
                        dominance: 0,
                        stakersPercentage: 50, // All active DLPs currently have 50%
                        isVerified: isVerified
                    };
                } else {
                    console.log(`DLP ${i} is empty or invalid`);
                    dlps[i] = {
                        id: i.toString(),
                        name: `DLP ${i}`,
                        logo: '',
                        website: '',
                        description: 'This DLP slot is not currently active',
                        stakeAmount: 0,
                        dominance: 0,
                        stakersPercentage: 0,
                        isVerified: false
                    };
                }
            } catch (error) {
                console.error(`Error processing DLP ${i}:`, error);
                dlps[i] = {
                    id: i.toString(),
                    name: `DLP ${i}`,
                    logo: '',
                    website: '',
                    description: 'Unable to load DLP data',
                    stakeAmount: 0,
                    dominance: 0,
                    stakersPercentage: 0,
                    isVerified: false
                };
            }
        }

        // Calculate dominance percentages
        const totalStakeInVana = Number(ethers.formatEther(totalStakeAmount));
        if (totalStakeInVana > 0) {
            dlps.forEach(dlp => {
                if (dlp) {
                    dlp.dominance = (dlp.stakeAmount / totalStakeInVana) * 100;
                }
            });
        }

        // Calculate Staking APY
        const stakingAPY = calculateStakingAPY(epochRewardAmount, totalStakeAmount);

        // Remove index 0 and any null entries
        let filteredDlps = dlps.slice(1).filter(dlp => dlp !== null);

        // Mark top 16 by stake amount
        filteredDlps = markTopStakes(filteredDlps);

        // Sort by stake amount descending by default
        filteredDlps = sortDLPs(filteredDlps, currentSort.column, currentSort.direction);

        // Store the data globally
        dlpData = {
            dlps: filteredDlps,
            totalDLPsCount: Number(totalDLPsCount),
            totalStakeAmount,
            epochRewardAmount,
            stakingAPY
        };

        return dlpData;
    } catch (error) {
        console.error('Error in getDLPs:', error);
        throw error;
    }
}

function formatNumber(num) {
    return Number(num).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
}

function getSortIcon(column) {
    if (currentSort.column !== column) {
        return '';
    }
    return currentSort.direction === 'asc' ? '↑' : '↓';
}

function createDLPRow(dlp) {
    // Add https:// to website if it doesn't have a protocol and the website exists
    const website = dlp.website ? (dlp.website.startsWith('http') ? dlp.website : `https://${dlp.website}`) : '';
    const websiteDisplay = website ? website.replace(/^https?:\/\//, '') : '-';
    
    return `
        <div class="table-row group">
            <div class="table-grid">
                <div class="table-cell table-cell-fixed text-dex-dark-200 font-medium">
                    <div class="flex items-center space-x-3">
                        <div class="w-8 h-8 rounded-full overflow-hidden bg-dex-dark-700 flex items-center justify-center flex-shrink-0">
                            <img
                                src="${dlp.logo}"
                                alt="${dlp.name} Logo"
                                class="w-full h-full object-cover"
                                onerror="this.parentElement.innerHTML = '<div class=\'w-full h-full flex items-center justify-center text-dex-dark-400 text-xs font-semibold\'>${dlp.name[0]}</div>'"
                            />
                        </div>
                        <span class="${dlp.isTopStake ? 'font-bold' : ''}">${dlp.name}</span>
                    </div>
                </div>
                <div class="table-cell text-dex-dark-300">${formatNumber(dlp.stakeAmount)}</div>
                <div class="table-cell text-dex-dark-300">${formatNumber(dlp.dominance)}%</div>
                <div class="table-cell text-dex-dark-300">${formatNumber(dlp.stakersPercentage)}%</div>
                <div class="table-cell">
                    ${dlp.isVerified ?
                        '<span class="verified-badge">✓ Verified</span>' :
                        '<span class="unverified-badge">✗ Not Verified</span>'
                    }
                </div>
                <div class="table-cell">
                    ${website ?
                        `<a href="${website}" target="_blank" rel="noopener noreferrer" class="link">
                            ${websiteDisplay}
                        </a>` :
                        `<span class="text-dex-dark-400">-</span>`
                    }
                </div>
                <div class="table-cell text-dex-dark-400 truncate">${dlp.description}</div>
            </div>
        </div>
    `;
}

function updateTable() {
    const tableBody = document.getElementById('dlp-table-body');
    let displayDlps = [...dlpData.dlps];

    if (currentSort.column) {
        displayDlps = sortDLPs(displayDlps, currentSort.column, currentSort.direction);
    }

    tableBody.innerHTML = displayDlps.map(dlp => createDLPRow(dlp)).join('');
    updateSortIndicators();
}

function updateSortIndicators() {
    const nameHeader = document.querySelector('[data-sort="name"]');
    const stakeAmountHeader = document.querySelector('[data-sort="stakeAmount"]');
    const dominanceHeader = document.querySelector('[data-sort="dominance"]');

    if (nameHeader) {
        nameHeader.innerHTML = `Name ${getSortIcon('name')}`;
    }

    if (stakeAmountHeader) {
        stakeAmountHeader.innerHTML = `Stake Amount ${getSortIcon('stakeAmount')}`;
    }

    if (dominanceHeader) {
        dominanceHeader.innerHTML = `Dominance ${getSortIcon('dominance')}`;
    }
}

function handleSort(column) {
    if (currentSort.column === column) {
        // Toggle direction if same column
        currentSort.direction = currentSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
        // New column, default to descending
        currentSort.column = column;
        currentSort.direction = 'desc';
    }
    
    // Update the table with the new sort
    updateTable();
}

async function initializeDashboard() {
    const tableBody = document.getElementById('dlp-table-body');
    const totalDLPs = document.getElementById('total-dlps');
    const totalStake = document.getElementById('total-stake');
    const cycleReward = document.getElementById('cycle-reward');
    const stakingAPY = document.getElementById('staking-apy');
    const loading = document.getElementById('loading');

    try {
        // Show loading state
        loading.style.display = 'flex';
        tableBody.style.display = 'none';

        // Fetch initial data
        const data = await getDLPs();

        // Hide loading state
        loading.style.display = 'none';
        tableBody.style.display = 'block';

        if (data.dlps.length === 0) {
            tableBody.innerHTML = '<div class="px-6 py-4 text-sm text-dex-dark-400">No DLPs found</div>';
            totalDLPs.textContent = '0';
            totalStake.textContent = '0 $VANA';
            cycleReward.textContent = '0 $VANA';
            stakingAPY.textContent = '0 %';
            return;
        }

        // Setup click handlers for sortable columns
        document.querySelectorAll('[data-sort]').forEach(header => {
            header.onclick = () => handleSort(header.dataset.sort);
        });

        // Initial render
        updateTable();

        totalDLPs.textContent = `${data.totalDLPsCount}`;
        totalStake.textContent = `${formatNumber(Number(ethers.formatEther(data.totalStakeAmount)))} $VANA`;
        cycleReward.textContent = `${formatNumber(Number(ethers.formatEther(data.epochRewardAmount)))} $VANA`;
        stakingAPY.textContent = `${formatNumber(data.stakingAPY)} %`;
    } catch (error) {
        console.error('Failed to initialize dashboard:', error);
        loading.style.display = 'none';
        tableBody.style.display = 'block';
        tableBody.innerHTML = `<div class="px-6 py-4 text-sm text-dex-accent-red">Error loading DLPs: ${error.message}</div>`;
        totalDLPs.textContent = 'Error loading DLPs';
        totalStake.textContent = 'Error loading total stake';
        cycleReward.textContent = 'Error loading cycle reward';
        stakingAPY.textContent = 'Error loading staking APY';
    }
}

// Make handleSort available globally
window.handleSort = handleSort;

document.addEventListener('DOMContentLoaded', initializeDashboard);